import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useT } from "../i18n";
import { X, ChevronLeft, ChevronRight, BookOpen } from "lucide-react";

export default function ComicsModal({ open, onClose }) {
  const { t } = useT();
  const [comics, setComics] = useState([]);
  const [active, setActive] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/public/comics").then(r => setComics(r.data)).catch(() => setComics([]));
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setActive(null);
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") { active !== null ? setActive(null) : onClose(); }
      if (active !== null && comics.length > 0) {
        if (e.key === "ArrowRight") setActive((active + 1) % comics.length);
        if (e.key === "ArrowLeft") setActive((active - 1 + comics.length) % comics.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, active, comics.length, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 backdrop-blur-sm px-4 py-8 bb-fade-up" data-testid="comics-modal">
      <div ref={panelRef} className="relative w-full max-w-6xl max-h-[90vh] overflow-auto bg-white rounded-xl shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-[var(--bb-border)] bg-white/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--bb-terracotta)] text-white grid place-items-center"><BookOpen size={16}/></div>
            <div>
              <div className="font-serif text-xl text-[var(--bb-forest)]">{t("comics_modal_title")}</div>
              <div className="text-xs text-[var(--bb-muted)]">{t("comics_modal_sub")}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-[var(--bb-border)] grid place-items-center hover:border-[var(--bb-terracotta)] hover:text-[var(--bb-terracotta)]" aria-label={t("comics_modal_close")} data-testid="comics-modal-close">
            <X size={16}/>
          </button>
        </div>

        {comics.length === 0 ? (
          <div className="p-16 text-center text-sm text-[var(--bb-muted)]">{t("comics_empty")}</div>
        ) : (
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {comics.map((c, i) => (
              <button
                key={c.comic_id}
                onClick={() => setActive(i)}
                className="group relative text-left"
                data-testid={`comic-card-${i}`}
              >
                <div className="aspect-[3/4] overflow-hidden rounded-lg border border-[var(--bb-border)] bg-[var(--bb-sand)]">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.image_path}`}
                    alt={c.title || "Bala Basava comic"}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                </div>
                {c.title && <div className="mt-2 text-xs text-[var(--bb-ink)] line-clamp-2">{c.title}</div>}
              </button>
            ))}
          </div>
        )}

        {/* Lightbox */}
        {active !== null && comics[active] && (
          <div className="fixed inset-0 z-[70] grid place-items-center bg-black/85 px-4 py-8" onClick={() => setActive(null)} data-testid="comics-lightbox">
            <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${comics[active].image_path}`}
                alt={comics[active].title || ""}
                className="w-full max-h-[80vh] object-contain rounded-lg"
              />
              {comics[active].title && (
                <div className="text-center mt-3 text-white text-sm">{comics[active].title}</div>
              )}
              <button onClick={() => setActive(null)} className="absolute -top-12 right-0 text-white/80 hover:text-white text-sm inline-flex items-center gap-1" data-testid="comics-lightbox-close"><X size={16}/> Close</button>
              {comics.length > 1 && (
                <>
                  <button onClick={() => setActive((active - 1 + comics.length) % comics.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="comics-prev"><ChevronLeft size={20}/></button>
                  <button onClick={() => setActive((active + 1) % comics.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="comics-next"><ChevronRight size={20}/></button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
